import { GoogleBusinessProfileIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './GoogleBusinessProfilePreview.module.scss'
import { RefObject, useEffect, useMemo } from 'react'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { highlightContent } from '../highlightContent'
import { MediaObject } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import cx from 'classnames'
import * as CSS from 'csstype'
import { collapseMultipleNewLines } from 'utils/string'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ReactNode } from 'react'

interface GoogleBusinessProfilePreviewProps {
  integration?: GoogleBusinessProfileIntegration
  content: string
  mediaObjects?: MediaObject[]
  loading?: boolean
  pollContentStream?: boolean
  hideConnectButton?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number | null
  showVideoControls?: boolean
  isActive?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  isGenerating?: boolean
  almanacFileKey?: string
  scheduledPost?: ScheduledPostShow
  onClose?: () => void
  alwaysTruncateCaption?: boolean
  postPreviewHeaderRef?: RefObject<HTMLDivElement>
  postPreviewContentRef?: RefObject<HTMLDivElement>
  postPreviewFooterRef?: RefObject<HTMLDivElement>
  onSeeMoreToggle?: (expanded: boolean) => void
  activePlatform?: ExternalIntegrationEnum
}

export const GoogleBusinessProfilePreview = ({
  mediaObjects,
  content,
  integration,
  loading,
  pollContentStream = true,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  maxWidth = 550,
  isActive = true,
  isInsertingMedia = false,
  isTransitioning = false,
  showHoverActionOverlay = false,
  isGenerating = false,
  almanacFileKey,
  scheduledPost,
  onClose,
  alwaysTruncateCaption = false,
  postPreviewHeaderRef,
  postPreviewContentRef,
  postPreviewFooterRef,
  onSeeMoreToggle,
  activePlatform,
}: GoogleBusinessProfilePreviewProps) => {
  const { name, picture } = getIntegrationDetails(integration)
  const { isMobileSm } = useResponsiveDesign()
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2')

  const formattedContent = useMemo<string>(() => collapseMultipleNewLines(content) ?? '', [content])

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: formattedContent,
    truncatedByDefault:
      alwaysTruncateCaption || (isPostPreviewV2 && (!!mediaObjects?.length || !!customPreviewContent)),
  })

  useEffect(() => {
    if (activePlatform && activePlatform !== ExternalIntegrationEnum.GoogleBusinessProfile && !isTruncated) {
      setIsTruncated(true)
    }
  }, [activePlatform, isTruncated, setIsTruncated])

  const highlightClassNames = {
    linkClassName: Styles.highlight,
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const userName = isMobileSm && name === 'Account Not Connected' ? 'Not Connected' : name

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  const firstMediaObject = mediaObjects?.[0]

  return (
    <div className={cx(Styles.container, containerClassName)} data-testid="gmb-preview" style={style}>
      <ConditionalWrapper
        condition={showHoverActionOverlay && almanacFileKey}
        wrapper={(children) => {
          if (!scheduledPost || !almanacFileKey) return children

          return (
            <EditCaptionOverlay
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFileKey}
              borderRadius={0}
              position="top-right"
              onClose={onClose}
            >
              {children}
            </EditCaptionOverlay>
          )
        }}
      >
        <div className={Styles.header} ref={postPreviewHeaderRef}>
          <img src={picture} alt={`${name || 'User'}'s profile`} className={Styles.profilePicture} />
          <div className={Styles.details}>
            <div className={Styles.primaryDetails}>
              <span className={Styles.profileName}>{userName}</span>

              {onEdit && (
                <div className={Styles.editButton}>
                  <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                    Edit
                  </Button>
                </div>
              )}
            </div>

            <div className={Styles.secondaryDetails}>
              <span className={Styles.timestamp}>3 days ago</span>
            </div>
          </div>
        </div>
        <div className={Styles.content} ref={postPreviewContentRef}>
          {(() => {
            if (isGenerating) return <CaptionSkeleton />
            if (loading && pollContentStream) {
              return (
                <PollingPreview
                  setImagesLoading={() => {}}
                  imagesLoading={false}
                  channel={ChannelType.FacebookPost}
                />
              )
            }
            return (
              <>
                <span
                  ref={textRef as React.RefObject<HTMLSpanElement>}
                  key={`caption-${formattedContent}`}
                  className={cx({ [Styles.truncated]: isTruncated })}
                  dangerouslySetInnerHTML={{
                    __html: highlightContent(formattedContent, highlightClassNames),
                  }}
                />
                {shouldShowMoreButton && !alwaysTruncateCaption && (
                  <MoreButton
                    isTruncated={isTruncated}
                    setIsTruncated={() => {
                      setIsTruncated(!isTruncated)
                      onSeeMoreToggle?.(isTruncated)
                    }}
                  />
                )}
              </>
            )
          })()}
        </div>
      </ConditionalWrapper>
      {customPreviewContent || (
        <div className={Styles.mediaContainer}>
          {firstMediaObject && (
            <MediaRenderer
              mediaObject={firstMediaObject}
              alt="Post media"
              showVideoControls={showVideoControls}
              isActive={isActive}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFileKey}
              isGenerating={isGenerating}
              onClose={onClose}
              isPressable={!showHoverActionOverlay}
            />
          )}
        </div>
      )}
      <div ref={postPreviewFooterRef}>
        <div className={Styles.ctaLink}>Learn More</div>
      </div>
    </div>
  )
}
